import React from 'react';

import { type ApiKeyPermissions as GeneratedAPIKeyPermissions } from '@column/api';
import { Icon } from '@column/column-ui-kit';

import {
  CustomRoleBankAccountLevelOverrides,
  CustomRoleEntityLevelOverrides,
  CustomRolePlatformLevelPermissions,
} from '~/typings/API';
import { keysToCamelCase } from '~/util/convertKeys';

import { setDefaultOverrides } from './utils';

export type APIKeyGranularPermissionLevel = 'default' | 'none' | 'read' | 'update' | 'write' | 'create-delete';
export type APIKeyBankAccountLevelOverrides = Omit<CustomRoleBankAccountLevelOverrides, 'thisBankAccount'> & {
  thisBankAccount?: APIKeyGranularPermissionLevel;
};
export type APIKeyEntityLevelOverrides = Omit<CustomRoleEntityLevelOverrides, 'bankAccounts'> & {
  bankAccounts?: APIKeyGranularPermissionLevel;
};
export type APIKeyPlatformLevelPermissions = Omit<CustomRolePlatformLevelPermissions, 'bankAccounts'> & {
  bankAccounts?: APIKeyGranularPermissionLevel;
};

export interface APIKeyPermissions {
  bankAccountLevelOverrides?: APIKeyBankAccountLevelOverrides[];
  entityLevelOverrides?: APIKeyEntityLevelOverrides[];
  platformLevelPermissions: APIKeyPlatformLevelPermissions;
}

export enum APIKeyPermissionPreset {
  ViewOnly = 'view-only',
  FullAccess = 'full-access',
  Custom = 'custom',
}

export const apiKeyPermissionPresetLabels: Record<APIKeyPermissionPreset, string> = {
  [APIKeyPermissionPreset.FullAccess]: 'Full Access',
  [APIKeyPermissionPreset.ViewOnly]: 'View Only',
  [APIKeyPermissionPreset.Custom]: 'Custom',
};

// Tinted the same way the roles permission pickers do: seafoam for full access, primary for view
// only, yellow for custom.
export const apiKeyPermissionPresetIcons: Record<APIKeyPermissionPreset, React.ReactNode> = {
  [APIKeyPermissionPreset.FullAccess]: <Icon.CircleCheck className="[--icon-color:var(--color-seafoam-600)]!" />,
  [APIKeyPermissionPreset.ViewOnly]: <Icon.Eye className="[--icon-color:var(--color-primary)]!" />,
  [APIKeyPermissionPreset.Custom]: <Icon.Filters className="[--icon-color:var(--color-yellow-600)]!" />,
};

export const apiKeyPermissionPresetTableIcons: Record<APIKeyPermissionPreset, React.ReactNode> = {
  [APIKeyPermissionPreset.FullAccess]: (
    <Icon.CircleCheck className="[--icon-color:var(--color-seafoam-600)]! [--icon-size:16px]!" />
  ),
  [APIKeyPermissionPreset.ViewOnly]: <Icon.Eye className="[--icon-color:var(--color-primary)]! [--icon-size:16px]!" />,
  [APIKeyPermissionPreset.Custom]: (
    <Icon.Filters className="[--icon-color:var(--color-yellow-600)]! [--icon-size:16px]!" />
  ),
};

export const apiKeyPermissionPresetOptions = [
  {
    icon: apiKeyPermissionPresetIcons[APIKeyPermissionPreset.FullAccess],
    label: apiKeyPermissionPresetLabels[APIKeyPermissionPreset.FullAccess],
    value: APIKeyPermissionPreset.FullAccess,
  },
  {
    icon: apiKeyPermissionPresetIcons[APIKeyPermissionPreset.ViewOnly],
    label: apiKeyPermissionPresetLabels[APIKeyPermissionPreset.ViewOnly],
    value: APIKeyPermissionPreset.ViewOnly,
  },
  {
    icon: apiKeyPermissionPresetIcons[APIKeyPermissionPreset.Custom],
    label: apiKeyPermissionPresetLabels[APIKeyPermissionPreset.Custom],
    value: APIKeyPermissionPreset.Custom,
  },
];

export const apiKeyVisiblePlatformPermissionKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bankAccounts',
  'bookTransfers',
  'counterparties',
  'depositChecks',
  'entities',
  'issueChecks',
  'loans',
  'realtimeTransfers',
  'reporting',
  'stopPayments',
  'wireTransfers',
] as const satisfies readonly (keyof APIKeyPermissions['platformLevelPermissions'])[];

export const apiKeyVisibleBankAccountLevelOverrideKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bookTransfers',
  'depositChecks',
  'issueChecks',
  'realtimeTransfers',
  'stopPayments',
  'thisBankAccount',
  'wireTransfers',
] as const;

export const apiKeyVisibleEntityLevelOverrideKeys = [
  'achCredits',
  'achDebits',
  'achReturns',
  'bankAccounts',
  'bookTransfers',
  'depositChecks',
  'issueChecks',
  'realtimeTransfers',
  'stopPayments',
  'thisEntity',
  'wireTransfers',
] as const;

export const createAPIKeyPlatformLevelPermissions = (
  permissionLevel: 'read' | 'write',
  platformLevelPermissions?: APIKeyPermissions['platformLevelPermissions']
): APIKeyPermissions['platformLevelPermissions'] => {
  return {
    achCredits: platformLevelPermissions?.achCredits ?? permissionLevel,
    achDebits: platformLevelPermissions?.achDebits ?? permissionLevel,
    achReturns: platformLevelPermissions?.achReturns ?? permissionLevel,
    bankAccounts: platformLevelPermissions?.bankAccounts ?? permissionLevel,
    bookTransfers: platformLevelPermissions?.bookTransfers ?? permissionLevel,
    counterparties: platformLevelPermissions?.counterparties ?? permissionLevel,
    depositChecks: platformLevelPermissions?.depositChecks ?? permissionLevel,
    entities: platformLevelPermissions?.entities ?? permissionLevel,
    issueChecks: platformLevelPermissions?.issueChecks ?? permissionLevel,
    loans: platformLevelPermissions?.loans ?? permissionLevel,
    realtimeTransfers: platformLevelPermissions?.realtimeTransfers ?? permissionLevel,
    reporting: platformLevelPermissions?.reporting ?? 'read',
    stopPayments: platformLevelPermissions?.stopPayments ?? permissionLevel,
    wireTransfers: platformLevelPermissions?.wireTransfers ?? permissionLevel,
  };
};

export const apiKeyPermissionsToForm = (
  permissions: APIKeyPermissions | GeneratedAPIKeyPermissions
): APIKeyPermissions => keysToCamelCase(permissions) as APIKeyPermissions;

export const normalizeAPIKeyPermissions = (
  permissions?: APIKeyPermissions | GeneratedAPIKeyPermissions | null
): APIKeyPermissions => {
  const formPermissions = permissions ? apiKeyPermissionsToForm(permissions) : undefined;

  return {
    bankAccountLevelOverrides: setDefaultOverrides(
      formPermissions?.bankAccountLevelOverrides,
      'bankAccountId',
      apiKeyVisibleBankAccountLevelOverrideKeys
    ),
    entityLevelOverrides: setDefaultOverrides(
      formPermissions?.entityLevelOverrides,
      'entityId',
      apiKeyVisibleEntityLevelOverrideKeys
    ),
    platformLevelPermissions: createAPIKeyPlatformLevelPermissions('write', formPermissions?.platformLevelPermissions),
  };
};

export const getAPIKeyPermissionPreset = (
  platformLevelPermissions?: APIKeyPermissions['platformLevelPermissions']
): APIKeyPermissionPreset => {
  if (
    platformLevelPermissions &&
    apiKeyVisiblePlatformPermissionKeys.every((permission) => platformLevelPermissions[permission] === 'read')
  ) {
    return APIKeyPermissionPreset.ViewOnly;
  }
  if (
    platformLevelPermissions &&
    apiKeyVisiblePlatformPermissionKeys.every((permission) =>
      permission === 'reporting'
        ? platformLevelPermissions[permission] === 'read'
        : platformLevelPermissions[permission] === 'write'
    )
  ) {
    return APIKeyPermissionPreset.FullAccess;
  }
  return APIKeyPermissionPreset.Custom;
};

export const getAPIKeyPermissionPresetFromPermissions = (permissions: APIKeyPermissions): APIKeyPermissionPreset => {
  if ((permissions.bankAccountLevelOverrides?.length ?? 0) > 0 || (permissions.entityLevelOverrides?.length ?? 0) > 0) {
    return APIKeyPermissionPreset.Custom;
  }

  return getAPIKeyPermissionPreset(permissions.platformLevelPermissions);
};

// A key with no backing role is unrestricted, which the presets express as full access.
export const getAPIKeyPermissionPresetFromResponse = (
  permissions?: GeneratedAPIKeyPermissions | null
): APIKeyPermissionPreset =>
  permissions
    ? getAPIKeyPermissionPresetFromPermissions(normalizeAPIKeyPermissions(permissions))
    : APIKeyPermissionPreset.FullAccess;
